import { useMemo } from "react";
import type { GossipItem } from "@/mocks/gossip";
import { categories } from "@/mocks/gossip";

interface Props {
  items: GossipItem[];
  active: string;
  onCategory: (key: string) => void;
  onRead: (item: GossipItem) => void;
}

function toWan(n: number) {
  return n >= 10000 ? `${(n / 10000).toFixed(1)}w` : `${n}`;
}

export default function NewsSection({ items, active, onCategory, onRead }: Props) {
  const filtered = useMemo(
    () => (active === "all" ? items : items.filter((i) => i.categoryKey === active)),
    [items, active]
  );

  return (
    <section id="hot" className="mx-auto max-w-[1440px] scroll-mt-20 px-4 py-10 md:px-8 md:py-14">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="flex items-center gap-2 text-sm font-semibold text-secondary-600">
            <i className="ri-flashlight-line text-base" aria-hidden="true"></i>
            GOSSIP FEED
          </p>
          <h2 className="mt-2 text-3xl font-bold text-foreground-950 md:text-4xl">今日瓜田 · 新鲜上架</h2>
          <p className="mt-2 max-w-2xl text-sm text-foreground-600">
            按栏目分类滚动更新的最新热料，看到感兴趣的标题，点开即可展开完整报道与来龙去脉。
          </p>
        </div>

        <div className="flex flex-wrap gap-1 rounded-full bg-background-200/80 p-1" role="tablist" aria-label="栏目筛选">
          {categories.map((c) => {
            const count = c.key === "all" ? items.length : items.filter((i) => i.categoryKey === c.key).length;
            return (
              <button
                key={c.key}
                type="button"
                role="tab"
                aria-selected={active === c.key}
                onClick={() => onCategory(c.key)}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  active === c.key
                    ? "bg-background-50 text-primary-700 shadow-none"
                    : "text-foreground-600 hover:text-foreground-900"
                }`}
              >
                {c.name}
                <span className={`ml-1 text-xs ${active === c.key ? "text-secondary-500" : "text-foreground-400"}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="mt-8 rounded-3xl border border-dashed border-background-300 bg-background-100/60 px-6 py-14 text-center">
          <i className="ri-inbox-2-line text-3xl text-background-400" aria-hidden="true"></i>
          <p className="mt-3 text-sm text-foreground-600">这个瓜棚暂时还在上货，换一个栏目逛逛吧。</p>
        </div>
      ) : (
        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((item) => (
            <article
              key={item.id}
              className="group flex flex-col overflow-hidden rounded-3xl bg-background-100 transition-transform duration-300 hover:-translate-y-1"
            >
              <button
                type="button"
                onClick={() => onRead(item)}
                className="relative block overflow-hidden text-left"
                aria-label={`阅读：${item.title}`}
              >
                <img
                  src={item.cover}
                  alt={item.title}
                  className="h-44 w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-foreground-950/70 px-3 py-1 text-[11px] font-medium text-background-50 backdrop-blur-sm">
                  <i className="ri-fire-fill text-xs text-secondary-400" aria-hidden="true"></i>
                  {item.tag}
                </span>
              </button>

              <div className="flex flex-1 flex-col p-5">
                <p className="text-xs font-semibold text-primary-600">
                  {item.category}
                  <span className="mx-2 text-background-400">|</span>
                  <span className="font-normal text-foreground-400">{item.time}</span>
                </p>
                <button
                  type="button"
                  onClick={() => onRead(item)}
                  className="mt-2 text-left text-lg font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700"
                >
                  {item.title}
                </button>
                <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{item.summary}</p>

                <div className="mt-auto flex items-center justify-between pt-4 text-xs text-foreground-500">
                  <span className="inline-flex items-center gap-1.5">
                    <i className="ri-eye-line text-base" aria-hidden="true"></i>
                    {toWan(item.views)}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <i className="ri-chat-3-line text-base" aria-hidden="true"></i>
                    {toWan(item.comments)}
                  </span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRead(item);
                    }}
                    className="inline-flex items-center gap-1 text-primary-600 transition-colors hover:text-primary-700"
                    aria-label={`阅读 ${item.title}`}
                  >
                    阅读全文
                    <i className="ri-arrow-right-line text-sm leading-none" aria-hidden="true"></i>
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}